import { useState } from 'react'
import { Button, EditableTable, TableActionButton } from '@tunasong/ui-lib'
import type { EditableColumn } from '@tunasong/ui-lib'
import { Add, Cancel, Delete, Play, Save } from '@tunasong/icons'
import { shortUuid } from '@tunasong/models'
import type { MidiSpec } from '@tunasong/schemas'
import { useMidi } from './midi.hook.js'
import styles from './midi-editor.shared.module.css'

interface PCRow {
  id: string
  name: string
  pc: number
  bank?: number
  bankLsb?: number
  isNew: boolean
}

const toRows = (midi: MidiSpec): PCRow[] =>
  midi.programs.map((program, index) => ({
    id: index.toString(),
    name: program.name,
    pc: program.pc,
    bank: program.bank !== undefined ? Number(program.bank) : undefined,
    bankLsb: program.bankLsb !== undefined ? Number(program.bankLsb) : undefined,
    isNew: false,
  }))

const columns: EditableColumn<PCRow>[] = [
  { field: 'name', headerName: 'Name', minWidth: 250, editable: true },
  { field: 'pc', headerName: 'Program Change', type: 'number', minWidth: 100, editable: true },
  { field: 'bank', headerName: 'Bank (CC: 0)', type: 'number', editable: true },
  { field: 'bankLsb', headerName: 'Bank LSB (CC: 32)', type: 'number', editable: true },
]

export interface MidiGridProps {
  /** use the specified MIDI channel. If not specified, use Omni (all channels) */
  channel?: number
  spec: MidiSpec
  onChange(spec: MidiSpec): void
  onChannelChange?(channel: number): void
}

export function MidiPCEditor({ spec, onChange, channel }: MidiGridProps) {
  const [rows, setRows] = useState(toRows(spec))
  const [newRowId, setNewRowId] = useState<string | null>(null)
  const { sendProgramChange } = useMidi({ channel })

  const saveRows = (updated: PCRow[]) => {
    onChange({
      ...spec,
      programs: updated.map(row => ({
        name: row.name,
        pc: Number(row.pc),
        bank: row.bank !== undefined ? Number(row.bank) : undefined,
        bankLsb: row.bankLsb !== undefined ? Number(row.bankLsb) : undefined,
      })),
    })
  }

  const handleAdd = () => {
    const id = shortUuid()
    const newRow: PCRow = { id, name: '', pc: 0, bank: 0, isNew: true }
    setRows(prev => [...prev, newRow])
    setNewRowId(id)
  }

  const handleRowUpdate = (updatedRow: PCRow) => {
    const updatedRows = rows.map(row => (row.id === updatedRow.id ? { ...updatedRow, isNew: false } : row))
    setRows(updatedRows)
    saveRows(updatedRows)
  }

  const handleDelete = (row: PCRow) => {
    const updatedRows = rows.filter(r => r.id !== row.id)
    setRows(updatedRows)
    saveRows(updatedRows)
  }

  const handlePlay = (row: PCRow) => {
    sendProgramChange({ program: row.pc, patchBank: row.bank, patchBankLsb: row.bankLsb })
  }

  return (
    <EditableTable
      rows={rows}
      columns={columns}
      autoEditId={newRowId}
      onRowUpdate={handleRowUpdate}
      toolbar={
        <div className={styles.toolbar}>
          <Button color="primary" startIcon={<Add />} onClick={handleAdd}>
            Add Program
          </Button>
        </div>
      }
      renderActions={row => (
        <>
          <TableActionButton icon={<Play />} label="Play" onClick={() => handlePlay(row)} />
          <TableActionButton icon={<Delete />} label="Delete" onClick={() => handleDelete(row)} />
        </>
      )}
      renderEditActions={(_row, onSave, onCancel) => (
        <>
          <TableActionButton icon={<Save />} label="Save" onClick={onSave} />
          <TableActionButton icon={<Cancel />} label="Cancel" onClick={onCancel} />
        </>
      )}
    />
  )
}
